export const STRATEGIC_PROJECTS = [
  {
    id: 'Aria',
    dashboardName: 'Aria',
    agendaBaseLabel: 'aria',
    fill: '#8da0cb',
    ink: '#1f2937',
    marketPlan: 6,
    bmrPlan: 0
  },
  {
    id: 'Fusion',
    dashboardName: 'Fusion',
    agendaBaseLabel: 'fusion',
    fill: '#fdbf6f',
    ink: '#1f2937',
    marketPlan: 0,
    bmrPlan: 0
  },
  {
    id: 'SoMi A',
    dashboardName: 'SoMi A',
    agendaBaseLabel: 'somi bldg A',
    fill: '#a6d854',
    ink: '#1f2937',
    marketPlan: 30,
    bmrPlan: 0
  },
  {
    id: 'SoMi B',
    dashboardName: 'SoMi B',
    agendaBaseLabel: 'somi bldg B',
    fill: '#66c2a5',
    ink: '#ffffff',
    marketPlan: 0,
    bmrPlan: 2
  },
  {
    id: 'SoMi Towns',
    dashboardName: 'SoMi Towns',
    agendaBaseLabel: 'somi towns',
    fill: '#ffffff',
    ink: '#1f2937',
    marketPlan: 38,
    bmrPlan: null
  },
  {
    id: 'Vida',
    dashboardName: 'Vida',
    agendaBaseLabel: 'vida',
    fill: '#fc8d62',
    ink: '#ffffff',
    marketPlan: 6,
    bmrPlan: null
  }
]

export const STRATEGIC_BMR_PROJECT_IDS = ['Aria', 'Fusion', 'SoMi A', 'SoMi B']

const CURRENT_YEAR_INVENTORY_STATUS = 4
const CURRENT_YEAR_BACKLOG_STATUS = 2
const CLOSED_STATUS = 1

export function numericStatusForUnit(unit) {
  const value = unit?.sales_status?.statusnumeric ?? unit?.status
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 5
}

function uniquenessText(unit) {
  return String(unit?.uniqueness ?? '').trim().toUpperCase()
}

function isBmrUnit(unit) {
  return uniquenessText(unit).includes('BMR')
}

function isModelUnit(unit) {
  return uniquenessText(unit).includes('MODEL')
}

function yearFromIso(value) {
  if (!value || typeof value !== 'string') return null
  const match = value.match(/^(\d{4})-\d{2}-\d{2}$/)
  return match ? Number(match[1]) : null
}

function isOpsProjectedIntoYear(unit, currentYear) {
  return yearFromIso(unit?.ops_projected_coe) === currentYear
}

function isBacklogInCurrentYearUniverse(unit, currentYear) {
  const sales = unit?.sales_status || {}
  const explicitYear =
    yearFromIso(sales.extended_adjusted_coe) ??
    yearFromIso(unit?.ops_projected_coe) ??
    yearFromIso(sales.coe_date) ??
    yearFromIso(sales.projected_closing_date)

  if (explicitYear == null) return true
  return explicitYear <= currentYear
}

function isModelInCurrentYearUniverse(unit, currentYear) {
  const statusNumeric = numericStatusForUnit(unit)
  if (statusNumeric === CLOSED_STATUS) return false

  if (statusNumeric === 5) {
    return isOpsProjectedIntoYear(unit, currentYear)
  }

  return true
}

export function buildUniverseCounts(units, bmrOnly, currentYear) {
  return units.reduce((acc, unit) => {
    if (isBmrUnit(unit) !== bmrOnly) return acc

    const statusNumeric = numericStatusForUnit(unit)
    if (statusNumeric === CLOSED_STATUS) return acc

    if (isModelUnit(unit)) {
      if (isModelInCurrentYearUniverse(unit, currentYear)) {
        acc.models += 1
      }
      return acc
    }

    if (statusNumeric === CURRENT_YEAR_BACKLOG_STATUS) {
      if (isBacklogInCurrentYearUniverse(unit, currentYear)) {
        acc.backlog += 1
      }
      return acc
    }

    if (statusNumeric === CURRENT_YEAR_INVENTORY_STATUS) {
      acc.inventory += 1
      return acc
    }

    if (statusNumeric === 5 && isOpsProjectedIntoYear(unit, currentYear)) {
      acc.inventory += 1
    }

    return acc
  }, { inventory: 0, backlog: 0, models: 0 })
}

function buildRow(project, counts, segment) {
  const segmentSuffix = segment === 'bmr' ? 'BMR' : 'MKT'
  const plan = segment === 'bmr' ? (project.bmrPlan ?? 0) : project.marketPlan
  return {
    key: `${project.id}-${segment}`,
    projectId: project.id,
    segment,
    label: `${project.dashboardName} -${segment === 'bmr' ? 'BMR' : 'Mkt.'}`,
    agendaLabel: `${project.agendaBaseLabel} - ${segmentSuffix}`,
    fill: project.fill,
    ink: project.ink,
    plan,
    total: counts.inventory + counts.backlog + counts.models,
    ...counts
  }
}

export function buildStrategicCountdownSnapshot(timelineByProject, currentYear) {
  const marketRows = STRATEGIC_PROJECTS.map(project => {
    const counts = buildUniverseCounts(timelineByProject[project.id] || [], false, currentYear)
    return buildRow(project, counts, 'market')
  })

  const bmrRows = STRATEGIC_PROJECTS
    .filter(project => STRATEGIC_BMR_PROJECT_IDS.includes(project.id))
    .map(project => {
      const counts = buildUniverseCounts(timelineByProject[project.id] || [], true, currentYear)
      return buildRow(project, counts, 'bmr')
    })

  const rows = [...marketRows, ...bmrRows]
  const totalRow = rows.reduce((acc, row) => ({
    inventory: acc.inventory + row.inventory,
    backlog: acc.backlog + row.backlog,
    models: acc.models + row.models,
    total: acc.total + row.total,
    plan: acc.plan + row.plan
  }), { inventory: 0, backlog: 0, models: 0, total: 0, plan: 0 })

  return {
    marketRows,
    bmrRows,
    rows,
    totalRow
  }
}
